import { XIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import styles from './Chip.module.scss';

export type TChipColour =
  | 'blue'
  | 'blueStrong'
  | 'blueSoft'
  | 'cyan'
  | 'crimson'
  | 'green'
  | 'orange'
  | 'amber'
  | 'purple'
  | 'red'
  | 'yellow'
  | 'grey'
  | 'clear'
  | 'white';
export type TChipSize = 'xxs' | 'xs' | 's' | 'm';

interface IChipProps extends Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'tabIndex'> {
  label: string;
  colour?: TChipColour;
  size?: TChipSize;
  outline?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  showDot?: boolean;
  onDelete?: () => void;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  className?: string;
}

export function Chip({
  label,
  colour = 'grey',
  size = 's',
  outline = false,
  leadingIcon,
  trailingIcon,
  showDot = false,
  onDelete,
  onClick,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  tabIndex,
}: IChipProps) {
  'use memo';

  const chipContent = (
    <>
      {leadingIcon && <span className={styles.leadingIcon}>{leadingIcon}</span>}

      <span className={styles.content}>
        {showDot && <span className={styles.dot} />}
        <span className={styles.label}>{label}</span>
      </span>

      {trailingIcon && <span className={styles.trailingIcon}>{trailingIcon}</span>}

      {onDelete && (
        <button
          type="button"
          className={styles.deleteButton}
          onClick={e => {
            e.stopPropagation();
            onDelete?.();
          }}
          disabled={disabled}
          aria-label={`Remove ${label}`}
        >
          <XIcon weight="bold" />
        </button>
      )}
    </>
  );

  const chipClasses = classNames(
    styles.chip,
    styles[colour],
    styles[size],
    outline && styles.outline,
    disabled && styles.disabled,
    onClick && styles.interactive,
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={chipClasses}
        onClick={onClick}
        disabled={disabled}
        data-disabled={disabled || undefined}
        aria-label={ariaLabel}
        tabIndex={tabIndex}
      >
        {chipContent}
      </button>
    );
  }

  return (
    <span className={chipClasses} data-disabled={disabled || undefined}>
      {chipContent}
    </span>
  );
}
